import {
  ADMIN_DASHBOARD_ADD,
  APP_SECOND_NATIVE_ADD,
  APP_SINGLE_NATIVE_BASE,
  APP_WEB_ONLY_BASE,
  APP_WEB_SURFACE_ADD,
  BOOKING_PAYMENTS_ADD,
  ECOMMERCE_ADD,
  COMPLEXITY_MULTIPLIER,
  GLOBAL_MIN_FLOOR_GBP,
  INTEGRATIONS_ADD,
  MULTI_SURFACE_OVERHEAD,
  OPEN_ENDED_THRESHOLD_GBP,
  RANGE_ROUND_STEP_GBP,
  SYSTEM_BASE,
  USER_ACCOUNTS_ADD,
  WEBSITE_TIER_BASE,
} from "@/lib/project-estimator-config";
import type {
  EstimatorEcommerce,
  EstimatorWebsiteTier,
  ProjectEstimateResult,
  ProjectEstimatorInputs,
} from "@/lib/project-estimator-types";
/**
 * Single source for the monthly care plan prices on this site, itself a documented mirror of the
 * Stripe-backed definitions in lead-engine. Imported rather than duplicated so the estimator and the
 * quote builder can never quote two different figures for the same plan.
 */
import {
  RETAINER_TIERS,
  retainerTierFromSignals,
  type RetainerTier,
} from "@/lib/quote-selection";

/**
 * Why this build landed on this plan, so the figure never appears without a reason next to it.
 * Someone who understands why they are on Operator will not read it as an upsell.
 */
const RETAINER_REASON: Record<RetainerTier, string> = {
  starter: "Your build is a website, so it needs hosting, updates and security looking after.",
  growth:
    "You are taking bookings or payments, so the money side needs watching as well as the site itself.",
  operator:
    "You are having a system built with accounts, dashboards or integrations in it, so there is more to keep running and more that can break.",
  platform:
    "Several kinds of user will be in this every day, so there is more to keep running and more that stops work when it breaks.",
  largePlatform:
    "This is heavy daily use with payments running through it, so it needs priority support behind it.",
};

function roundToStep(n: number, step: number, mode: "floor" | "ceil"): number {
  const f = mode === "floor" ? Math.floor : Math.ceil;
  return f(n / step) * step;
}

function formatGbp(n: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(n);
}

/**
 * Choosing "Online shop" but leaving the depth on "none" is a contradiction the UI lets a visitor
 * reach, and pricing it as a plain brochure site would quote a shop thousands too low.
 */
export function effectiveEcommerce(input: ProjectEstimatorInputs): EstimatorEcommerce {
  if (input.primary === "ecommerce" && input.ecommerce === "none") return "standard";
  return input.ecommerce;
}

function resolveScope(input: ProjectEstimatorInputs): {
  website: boolean;
  system: boolean;
  app: boolean;
} {
  if (input.primary === "website") {
    return { website: true, system: false, app: false };
  }
  /* A shop is a website that sells, so it rides the website stream. */
  if (input.primary === "ecommerce") {
    return { website: true, system: false, app: false };
  }
  if (input.primary === "system") {
    return { website: false, system: true, app: false };
  }
  if (input.primary === "app") {
    return { website: false, system: false, app: true };
  }
  const { combination: c } = input;
  const picked = [c.website, c.system, c.app].filter(Boolean).length;
  /** One surface selected: treat like a single-product estimate. */
  if (picked === 1) {
    return { website: c.website, system: c.system, app: c.app };
  }
  /** None selected: sensible default so the tool never returns an empty band. */
  if (picked === 0) {
    return { website: true, system: true, app: false };
  }
  return { website: c.website, system: c.system, app: c.app };
}

function combineStreams(
  parts: Array<{ min: number; max: number }>,
): { min: number; max: number } {
  const nonEmpty = parts.filter((p) => p.min > 0 || p.max > 0);
  if (nonEmpty.length === 0) return { min: 0, max: 0 };
  if (nonEmpty.length === 1) return nonEmpty[0];

  const sorted = [...nonEmpty].sort((a, b) => b.max - a.max);
  let min = sorted[0].min;
  let max = sorted[0].max;
  for (let i = 1; i < sorted.length; i++) {
    const weight = i === 1 ? 0.5 : 0.42;
    min += sorted[i].min * weight;
    max += sorted[i].max * weight;
  }
  const n = sorted.length as 2 | 3;
  if (n === 2 || n === 3) {
    min += MULTI_SURFACE_OVERHEAD[n].min;
    max += MULTI_SURFACE_OVERHEAD[n].max;
  }
  return { min, max };
}

/**
 * The tier a website is REALLY on, which is not always the one that was picked.
 *
 * Tom's rule, 2026-09-06: "Tier is set by capability, not size. A site with 60 pages that only
 * generates enquiries is Business. A five-page site that takes payments is Advanced."
 *
 * The second half of that has to be enforced rather than trusted, because the estimator lets
 * somebody choose "Presence" and then tick payments, and quoting £1,000 for a site with a
 * checkout is a number we would have to walk back on the call. So a transacting capability
 * promotes the tier no matter what was selected. It only ever moves UP.
 */
export function resolveWebsiteTier(input: ProjectEstimatorInputs): EstimatorWebsiteTier {
  const transacts =
    effectiveEcommerce(input) !== "none" ||
    input.bookingPayments !== "none" ||
    input.userAccounts;
  if (transacts) return "transacts";

  /* An admin side means somebody is managing content or records, which is past a brochure. */

  return input.websiteTier;
}

function estimateWebsiteStream(input: ProjectEstimatorInputs): {
  min: number;
  max: number;
} {
  const tier = WEBSITE_TIER_BASE[resolveWebsiteTier(input)];
  let min = tier.min;
  let max = tier.max;

  const booking = BOOKING_PAYMENTS_ADD[input.bookingPayments];
  min += booking.min;
  max += booking.max;

  const shop = ECOMMERCE_ADD[effectiveEcommerce(input)];
  min += shop.min;
  max += shop.max;

  /* Accounts and admin are added once after the streams. Adding them here would bill twice. */
  return { min, max };
}

function estimateSystemStream(): { min: number; max: number } {
  return { min: SYSTEM_BASE.min, max: SYSTEM_BASE.max };
}

function estimateAppStream(input: ProjectEstimatorInputs): {
  min: number;
  max: number;
} {
  const { platformIos, platformAndroid, platformWeb } = input;
  const nativeCount = (platformIos ? 1 : 0) + (platformAndroid ? 1 : 0);

  if (nativeCount === 0) {
    if (!platformWeb) {
      return { min: 0, max: 0 };
    }
    return { min: APP_WEB_ONLY_BASE.min, max: APP_WEB_ONLY_BASE.max };
  }

  let min = APP_SINGLE_NATIVE_BASE.min;
  let max = APP_SINGLE_NATIVE_BASE.max;

  if (nativeCount === 2) {
    min += APP_SECOND_NATIVE_ADD.min;
    max += APP_SECOND_NATIVE_ADD.max;
  }

  if (platformWeb) {
    min += APP_WEB_SURFACE_ADD.min;
    max += APP_WEB_SURFACE_ADD.max;
  }

  return { min, max };
}

function surfaceSpread(website: boolean, system: boolean, app: boolean): number {
  const n = [website, system, app].filter(Boolean).length;
  if (n <= 1) return 1;
  if (n === 2) return 1.2;
  return 1.32;
}

function estimateTimelineWeeks(input: ProjectEstimatorInputs, scope: {
  website: boolean;
  system: boolean;
  app: boolean;
}): { min: number; max: number } {
  let minW = 4;
  let maxW = 8;

  if (scope.website) {
    /* Timeline follows what the site DOES, like the price does. A brochure is quick whatever its
       page count; a site that takes bookings and payments has flows to build and test. It used
       to key off a page band, which made a sixty page brochure look slower than a five page
       checkout, and the opposite is true. */
    const tier = resolveWebsiteTier(input);
    minW += tier === "presence" ? 1 : tier === "sells" ? 2 : 3;
    maxW += tier === "presence" ? 2 : tier === "sells" ? 4 : 6;
    minW += 1;
    maxW += 2;
  }
  if (scope.system) {
    minW += 4;
    maxW += 10;
  }
  if (scope.app) {
    const native = (input.platformIos ? 1 : 0) + (input.platformAndroid ? 1 : 0);
    minW += native > 0 ? 8 : 5;
    maxW += native > 1 ? 16 : native > 0 ? 14 : 10;
    if (input.platformWeb && native > 0) {
      minW += 1;
      maxW += 3;
    }
  }

  const surfaces = [scope.website, scope.system, scope.app].filter(Boolean)
    .length;
  if (surfaces >= 2) {
    minW += 2;
    maxW += 5;
  }

  if (input.bookingPayments === "complex") {
    minW += 1;
    maxW += 3;
  }
  if (input.integrations === "heavy") {
    minW += 2;
    maxW += 5;
  }
  if (input.userAccounts) {
    minW += 1;
    maxW += 2;
  }
  if (input.adminDashboard) {
    minW += 1;
    maxW += 3;
  }

  const cm =
    input.complexity === "straightforward"
      ? 0.92
      : input.complexity === "moderate"
        ? 1
        : 1.12;
  minW = Math.round(minW * cm);
  maxW = Math.round(maxW * cm);

  /**
   * The care plan used to extend the delivery estimate by two weeks. It no longer does: the plan
   * starts after launch, so it cannot change how long the build takes.
   */
  minW = Math.max(3, minW);
  maxW = Math.max(minW + 2, maxW);

  return { min: minW, max: maxW };
}

/**
 * Returns a rough investment band and timeline hint from estimator inputs.
 * Not a quote — for qualification and expectation-setting only.
 */
/** The published entry price for apps and systems. Stated, not computed: see pricing-floors.ts. */
const CALL_SCOPED_GBP = 8000;

export function estimateProject(input: ProjectEstimatorInputs): ProjectEstimateResult {
  const scope = resolveScope(input);
  const spread = surfaceSpread(scope.website, scope.system, scope.app);

  const streams: Array<{ min: number; max: number }> = [];

  if (scope.website) {
    streams.push(estimateWebsiteStream(input));
  }

  if (scope.system) {
    streams.push(estimateSystemStream());
  }

  if (scope.app) {
    let appPart = estimateAppStream(input);
    /** No platform toggles yet — assume a web-led product until clarified. */
    if (appPart.min === 0 && appPart.max === 0) {
      appPart = {
        min: Math.round(APP_WEB_ONLY_BASE.min * 0.88),
        max: Math.round(APP_WEB_ONLY_BASE.max * 0.92),
      };
    }
    streams.push(appPart);
  }

  let { min, max } = combineStreams(streams);

  /** A standalone website account area needs identity and permissions beyond simple booking.
      Apps and systems include this foundation in their base stream. */
  if (input.userAccounts && scope.website && !scope.system && !scope.app) {
    min += USER_ACCOUNTS_ADD.min;
    max += USER_ACCOUNTS_ADD.max;
  }
  if (input.adminDashboard) {
    min += ADMIN_DASHBOARD_ADD.min * spread;
    max += ADMIN_DASHBOARD_ADD.max * spread;
  }

  /** Booking / payments on non-website surfaces (in-app, portal billing). */
  if (!scope.website && (scope.system || scope.app)) {
    const b = BOOKING_PAYMENTS_ADD[input.bookingPayments];
    if (b.min > 0 || b.max > 0) {
      const w = 0.48;
      min += b.min * w;
      max += b.max * w;
    }
  }

  const integ = INTEGRATIONS_ADD[input.integrations];
  min += integ.min;
  max += integ.max;

  const mult = COMPLEXITY_MULTIPLIER[input.complexity];
  min *= mult;
  max *= mult;

  /**
   * The care plan is deliberately NOT added here. It is a monthly subscription, the band below is a
   * one-off build cost, and adding the two together is what used to overstate quotes by thousands.
   * It is returned as its own figure instead.
   */
  min = Math.max(GLOBAL_MIN_FLOOR_GBP, min);
  max = Math.max(min + RANGE_ROUND_STEP_GBP, max);

  const openEnded = max >= OPEN_ENDED_THRESHOLD_GBP;
  const roundedMin = roundToStep(min, RANGE_ROUND_STEP_GBP, "floor");
  const roundedMax = openEnded
    ? roundToStep(Math.min(max, OPEN_ENDED_THRESHOLD_GBP), RANGE_ROUND_STEP_GBP, "ceil")
    : roundToStep(max, RANGE_ROUND_STEP_GBP, "ceil");

  /* Apps and internal systems are quoted on a call, not by this form. See ProjectEstimateResult.
     The arithmetic above still runs, because the timeline and the care plan are derived from it
     and because the internal figure is what a scoping call opens against; it is only the number
     shown to the reader that is withheld. */
  const callScoped = scope.app || scope.system;
  const rangeLabel = callScoped
    ? `From ${formatGbp(CALL_SCOPED_GBP)}, scoped on a call`
    : openEnded
      ? `${formatGbp(roundedMin)}+`
      : `${formatGbp(roundedMin)} to ${formatGbp(roundedMax)}`;

  const tw = estimateTimelineWeeks(input, scope);
  const timelineLabel = `Roughly ${tw.min} to ${tw.max} weeks from kickoff to launch-ready delivery, depending on approvals and content.`;

  /**
   * Which care plan this build lands on, derived from the same answers that priced the build. A
   * bigger, more moving-parts build costs more to look after, so the plan is a consequence of the
   * scope rather than a separate choice. Same rule the quote builder uses.
   */
  const tier = retainerTierFromSignals({
    system: scope.system,
    app: scope.app,
    ecommerce: effectiveEcommerce(input) !== "none",
    userAccounts: input.userAccounts,
    adminDashboard: input.adminDashboard,
    integrations: input.integrations !== "none",
    bookingPayments: input.bookingPayments !== "none",
  });
  const plan = RETAINER_TIERS[tier];

  return {
    minGbp: roundedMin,
    maxGbp: openEnded ? OPEN_ENDED_THRESHOLD_GBP : roundedMax,
    openEnded,
    rangeLabel,
    callScoped,
    timelineLabel,
    timelineWeeksMin: tw.min,
    timelineWeeksMax: tw.max,
    retainerMonthlyGbp: plan.monthlyGbp,
    retainerLabel: plan.label,
    retainerReason: RETAINER_REASON[tier],
  };
}
